import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  useCreateSponsor,
  useCreateSponsorAdminDualRole,
  useUpdateSponsorUserDetails,
  useGetSponsorDetails,
  useDeleteSponsorUserDetails,
  useGetAllProviderOrg,
  useGetProviderOrgDetails,
  useGetProviderDetailsWithUsers,
  useCreateProviderDualRole,
  useUpdateProviderUserRolesPermissions,
} from "../custom-hooks/queries";
import NotificationModal from "./NotificationModal";
import { useNavigate, useParams } from "react-router-dom";
import {
  countries,
  vendorRoleDefinitions,
  roleDefinitions,
} from "../utils/constants";
import ComboBox from "./ComboBox";
import OrganizationUsersGrid from "./OrganizationUsersGrid";
import { HttpsUrlInputStandalone } from "./HttpsUrlInput";

/** Reusable text input component */
// Special "None" option for creating new sponsor without existing vendor
const NONE_VENDOR_OPTION = {
  provider_id: null,
  hc_org_id: null,
  name: "None (Create new sponsor)",
  isNoneOption: true,
};

const FormInput = ({
  label,
  name,
  register,
  errors,
  placeholder,
  numeric,
  type = "text",
  options,
  disabled = false,
  className = "",
  setValue,
  watch,
  onClear,
}) => {
  if (type === "select") {
    // Check if options is an object with items (from API) or a plain array
    const dataArray = options?.items || options || [];

    // Only use ComboBox if setValue and watch are provided (for vendor organization)
    // Otherwise, use regular select for other dropdowns
    if (setValue && watch) {
      // Add "None" option at the beginning of the list
      const dataWithNone = [NONE_VENDOR_OPTION, ...dataArray];

      const handleSelection = (value) => {
        // If "None" option is selected or cleared, clear the selection and call onClear
        if (!value || value?.isNoneOption) {
          setValue(name, null);
          if (onClear) onClear();
        } else {
          setValue(name, value);
        }
      };

      return (
        <div className={`flex flex-col ${className}`}>
          <label className="block text-sm font-medium text-gray-900 mb-1">
            {label}
          </label>
          <ComboBox
            data={dataWithNone}
            placeHolder="Select Existing Vendor"
            setSelected={handleSelection}
            selected={watch(name)}
            width={"w-[700px]"}
            inputWidth={"w-[620px]"}
            disabled={disabled}
          />
          {errors[name] && (
            <p className="mt-1 text-sm text-red-600">{errors[name].message}</p>
          )}
        </div>
      );
    }

    // Fallback to regular select for other dropdowns
    return (
      <div className={`flex flex-col ${className}`}>
        <label className="block text-sm font-medium text-gray-900">
          {label}
        </label>
        <select
          {...register(name)}
          disabled={disabled}
          className="mt-1 ring-0 border-gray-300 rounded-md text-sm"
        >
          <option value="">-- Select --</option>
          {(dataArray || []).map((opt) => (
            <option
              key={typeof opt === "object" ? opt.value : opt}
              value={typeof opt === "object" ? opt.value : opt}
            >
              {typeof opt === "object" ? opt.label : opt}
            </option>
          ))}
        </select>
        {errors[name] && (
          <p className="mt-1 text-sm text-red-600">{errors[name].message}</p>
        )}
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${className}`}>
      <label className="block text-sm font-medium text-gray-900">{label}</label>
      <input
        type={type}
        {...register(name)}
        placeholder={placeholder}
        disabled={disabled}
        onKeyPress={
          numeric
            ? (e) => {
                if (!/^[0-9]*$/.test(e.key)) e.preventDefault();
              }
            : undefined
        }
        className={`mt-1 w-full border-gray-300 rounded-md text-sm ${
          disabled ? "bg-gray-100" : ""
        }`}
      />
      {errors[name] && (
        <p className="mt-1 text-sm text-red-600">{errors[name].message}</p>
      )}
    </div>
  );
};

/** Reusable radio group component for yes/no fields */
const RadioGroup = ({ label, name, register, errors }) => (
  <div className="flex flex-col">
    <label className="block text-sm font-medium text-gray-900">{label}</label>
    <div className="flex items-center gap-6 mt-1">
      {["yes", "no"].map((val) => (
        <label key={val} className="flex items-center">
          <input
            type="radio"
            {...register(name)}
            value={val}
            className="mr-2"
          />
          {val.charAt(0).toUpperCase() + val.slice(1)}
        </label>
      ))}
    </div>
    {errors[name] && (
      <p className="mt-1 text-sm text-red-600">{errors[name].message}</p>
    )}
  </div>
);

const SponsorForm = () => {
  // Helper function to normalize country names from API to match dropdown values
  const normalizeCountryName = (country) => {
    if (!country) return "";
    const countryMappings = {
      USA: "United States",
      US: "United States",
      "U.S.": "United States",
      "U.S.A.": "United States",
      "United States of America": "United States",
      UK: "United Kingdom",
      "U.K.": "United Kingdom",
      "Great Britain": "United Kingdom",
      GB: "United Kingdom",
      CA: "Canada",
      MX: "Mexico",
      AU: "Australia",
      NZ: "New Zealand",
      DE: "Germany",
      FR: "France",
      IT: "Italy",
      ES: "Spain",
      JP: "Japan",
      CN: "China",
      IN: "India",
      BR: "Brazil",
    };
    // Check if country matches any mapping (case-insensitive)
    const upperCountry = country.toUpperCase().trim();
    if (countryMappings[upperCountry]) {
      return countryMappings[upperCountry];
    }
    // Also check the original case
    if (countryMappings[country.trim()]) {
      return countryMappings[country.trim()];
    }
    // Return original if no mapping found (might already be in correct format)
    return country.trim();
  };

  // Auto-format office number to '1-234-567-8900'
  const handleOfficeNumberInput = (e) => {
    let value = e.target.value.replace(/[^\d]/g, ""); // allow digits only
    if (value.length > 11) value = value.slice(0, 11);
    let formatted = "";
    if (value.length > 0) {
      formatted += value[0];
    }
    if (value.length > 1) {
      formatted += "-";
      formatted += value.slice(1, 4);
    }
    if (value.length > 4) {
      formatted += "-";
      formatted += value.slice(4, 7);
    }
    if (value.length > 7) {
      formatted += "-";
      formatted += value.slice(7, 11);
    }
    e.target.value = formatted;
  };
  const { id } = useParams(); // Get id from route if present
  const navigate = useNavigate();

  // Define validation schema for organization fields
  const schema = yup.object().shape({
    organizationName: yup.string().required("Organization name is required"),
    vendorOrganization: yup.object().nullable(),
    companyWebsite: yup
      .string()
      .required("Company website is required")
      .test(
        "is-https",
        "Only HTTPS URLs are accepted for security compliance.",
        (value) => {
          if (!value || typeof value !== "string") return false;
          const v = value.trim().toLowerCase();
          if (v.startsWith("http://")) return false;
          if (v.startsWith("https://")) return true;
          try {
            new URL("https://" + value);
            return true;
          } catch {
            return false;
          }
        }
      ),
    officeNumber: yup
      .string()
      .matches(
        /^1-\d{3}-\d{3}-\d{4}$/,
        "Office number must be in the format 1-234-567-8900"
      ),
    companyEmail: yup
      .string()
      .email("Enter a valid email")
      .required("Company email is required"),
    companyType: yup.string().required("Company type is required"),
    country: yup.string().required("Country is required"),
    streetAddress: yup.string().required("Street address is required"),
    city: yup.string().required("City is required"),
    state: yup.string().required("State is required"),
    zipCode: yup
      .string()
      .nullable()
      .notRequired()
      .matches(/^[0-9a-zA-Z\s\-]+$/, {
        excludeEmptyString: true,
        message: "Enter a valid zip/postal code",
      }),
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors, isDirty },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onTouched",
  });

  // Get provider organizations (vendors) for the dropdown
  const { data: providerOrgData } = useGetAllProviderOrg(3500, 1, "");

  // Watch for vendor organization selection
  const selectedVendorOrg = watch("vendorOrganization");
  const selectedProviderId = selectedVendorOrg?.provider_id;
  const selectedOrgId = selectedVendorOrg?.hc_org_id;

  // Fetch provider organization details when a vendor is selected (only in create mode)
  const { data: providerOrgDetails, isSuccess: isProviderDetailsLoaded } =
    useGetProviderOrgDetails(selectedProviderId);

  // Fetch provider details including users when a vendor is selected (for users grid)
  const {
    data: providerDetailsWithUsers,
    isLoading: isProviderDetailsLoading,
    isError: isProviderDetailsError,
  } = useGetProviderDetailsWithUsers(selectedProviderId, selectedOrgId);

  // State for managing selected users and their roles
  const [selectedUsersWithRoles, setSelectedUsersWithRoles] = useState([]);

  // Notification modal state
  const [alertOpen, setAlertOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  // Handle role change for users
  const handleUserRoleChange = (userId, role) => {
    setSelectedUsersWithRoles((prev) => {
      const existingIndex = prev.findIndex((u) => u.userId === userId);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = { userId, role };
        return updated;
      }
      return [...prev, { userId, role }];
    });
  };

  // Clear all vendor-populated fields when "None" is selected or vendor is cleared
  const clearVendorFields = () => {
    setValue("organizationName", "", { shouldDirty: false });
    setValue("companyWebsite", "", { shouldDirty: false });
    setValue("companyEmail", "", { shouldDirty: false });
    setValue("streetAddress", "", { shouldDirty: false });
    setValue("city", "", { shouldDirty: false });
    setValue("country", "", { shouldDirty: false });
    setValue("state", "", { shouldDirty: false });
    setValue("zipCode", "", { shouldDirty: false });
    // Also clear selected users
    setSelectedUsersWithRoles([]);
  };

  // Fetch sponsor details if an id is present
  const { data: sponsorDetails, isSuccess: isDataLoaded } =
    useGetSponsorDetails(id);

  // Set vendor organization when in edit mode
  useEffect(() => {
    if (id && providerOrgData?.items?.length > 0 && sponsorDetails) {
      // Find the provider organization that matches the sponsor's vendor
      // You may need to adjust this logic based on how vendor is stored in sponsorDetails
      const selectedProvider = providerOrgData.items.find(
        (p) =>
          p.provider_id === sponsorDetails.provider_id ||
          p.name === sponsorDetails.vendor_name ||
          p.hc_org_id === sponsorDetails.hypercomply_org_id
      );
      if (selectedProvider) {
        setValue("vendorOrganization", selectedProvider);
      }
    }
  }, [id, providerOrgData, sponsorDetails, setValue]);

  // Prefill form with fetched sponsor details when updating
  useEffect(() => {
    if (id && isDataLoaded && sponsorDetails) {
      reset({
        organizationName: sponsorDetails.sponsor_name,
        companyWebsite: sponsorDetails.website,
        officeNumber: sponsorDetails.office_number || "",
        companyEmail: sponsorDetails.company_email || "",
        companyType: sponsorDetails.company_type || "",
        country: sponsorDetails.country,
        streetAddress: sponsorDetails.address1,
        city: sponsorDetails.city,
        state: sponsorDetails.state,
        zipCode: sponsorDetails.zip_code,
        vendorOrganization: sponsorDetails.vendorOrganization || null,
      });
    }
  }, [id, isDataLoaded, sponsorDetails, reset]);

  // Populate form fields when vendor organization is selected (on
  // ly in create mode)
  useEffect(() => {
    if (
      !id &&
      selectedProviderId &&
      isProviderDetailsLoaded &&
      providerOrgDetails
    ) {
      // Get primary location if available, otherwise use main provider data
      const primaryLocation = providerOrgDetails.provider_locations?.find(
        (loc) => loc.location_type === "primary"
      );

      // Get address data from primary location first, then fallback to main provider data
      const address1 =
        primaryLocation?.address1 || providerOrgDetails.address1 || "";
      const city = primaryLocation?.city || providerOrgDetails.city || "";
      const country =
        primaryLocation?.country || providerOrgDetails.country || "";
      const state = primaryLocation?.state || providerOrgDetails.state || "";
      const zip = primaryLocation?.zip || providerOrgDetails.zip || "";

      // Always update all fields when vendor changes (even with empty values to clear previous data)
      setValue("organizationName", providerOrgDetails.name || "", {
        shouldDirty: true,
      });
      setValue("companyWebsite", providerOrgDetails.url || "", {
        shouldDirty: true,
      });
      setValue("companyEmail", providerOrgDetails.email || "", {
        shouldDirty: true,
      });
      setValue("streetAddress", address1, { shouldDirty: true });
      setValue("city", city, { shouldDirty: true });
      // Normalize country name to match dropdown values (e.g., "USA" -> "United States")
      setValue("country", normalizeCountryName(country), { shouldDirty: true });
      setValue("state", state, { shouldDirty: true });
      setValue("zipCode", zip, { shouldDirty: true });
    }
  }, [
    selectedProviderId,
    isProviderDetailsLoaded,
    providerOrgDetails,
    id,
    setValue,
  ]);

  // Get the mutation hooks for creating, updating, and deleting
  const { mutate: createSponsor } = useCreateSponsor();
  const { mutate: createSponsorDualRole } = useCreateSponsorAdminDualRole();
  const { mutate: updateSponsor } = useUpdateSponsorUserDetails();
  const { mutate: deleteSponsor, isLoading: isDeleteLoading } =
    useDeleteSponsorUserDetails();
  const { mutate: createProviderDualRole } = useCreateProviderDualRole();
  const { mutate: updateProviderUserRoles } =
    useUpdateProviderUserRolesPermissions();

  // Helper to generate GUIDs for IDs when not present
  const generateGuid = () =>
    "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });

  const onSubmit = (data) => {
    setIsLoading(true);

    // Extract vendor organization information if selected
    const vendorName = data?.vendorOrganization?.name || "";
    const hyperComplyOrgId = data?.vendorOrganization?.hc_org_id || "";
    const providerId = data?.vendorOrganization?.provider_id || "";
    const organizationId = data?.vendorOrganization?.hc_org_id || null;

    // Check if vendor organization is selected (for dual role creation)
    const isVendorOrgSelected = !!data?.vendorOrganization?.provider_id;

    const sponsorData = {
      sponsor_name: data.organizationName,
      website: data.companyWebsite,
      office_number: data.officeNumber,
      company_email: data.companyEmail,
      company_type: data.companyType,
      address1: data.streetAddress,
      country: data.country,
      city: data.city,
      state: data.state,
      zip_code: data.zipCode,
      is_active: true,
      // Add vendor organization fields if needed by the API
      provider_id: providerId,
      vendor_name: vendorName,
      hypercomply_org_id: hyperComplyOrgId,
    };
    if (id) {
      // If an id exists, update the sponsor details

      updateSponsor(
        { id, userData: sponsorData },
        {
          onSuccess: () => {
            setIsLoading(false);
            setIsSuccess(true);
            setMessage("Organization updated successfully!");
            setAlertOpen(true);
            setTimeout(() => {
              navigate("/Backend/Users/Sponsors");
            }, 2000);
          },
          onError: (error) => {
            setIsLoading(false);
            setIsSuccess(false);
            setMessage(error.message || "Error updating organization");
            setAlertOpen(true);
          },
        }
      );
    } else {
      // Check if vendor organization is selected - use dual role API
      if (selectedVendorOrg && providerOrgDetails) {
        // Build dual role payload with only sponsor-level fields (per backend requirement)
        const dualRolePayload = {
          sponsor_id: providerDetailsWithUsers?.providerId || null,
          organization_id: selectedOrgId || null,
          sponsor_name: data.organizationName,
          address1: data.streetAddress,
          office_number: data.officeNumber,
          company_email: data.companyEmail,
          company_type: data.companyType,
          address2: data.streetAddress2 || data.address2 || "",
          country: data.country,
          city: data.city,
          state: data.state,
          zip_code: data.zipCode,
          website: data.companyWebsite,
          is_active: true,
          subscription_information: {
            has_rfi: true,
            existing_rfi_count: 10,
            existing_rfi_unlimited: false,
            new_rfi_count: 5,
            new_rfi_unlimited: false,
            has_vqa: true,
            available_new_vqa_count: 3,
            max_requests_per_month: 20,
            available_platform_credits: 100,
            has_rfi_scoring: true,
            has_vqa_scoring: false,
            reason_for_subscription_change:
              "Upgrade requested due to increased workload",
          },
        };

        // Build ordered sponsor_user_subroles and sponsor_user_permissions
        // using roleDefinitions (sponsor roles: "Sponsor Admin", "RFI Manager", "VQA Manager")
        const roleOrder = Object.keys(roleDefinitions || {});
        const sponsor_user_subroles = roleOrder.map((role) => ({
          sponsor_subrole_id: null,
          sub_role: role,
        }));

        const sponsor_user_permissions = [];
        roleOrder.forEach((role) => {
          const rights = roleDefinitions[role]?.defaultRights || [];
          rights.forEach((r) => {
            sponsor_user_permissions.push({
              rights: r,
              sponsor_rights_id: null,
            });
          });
        });

        // Attach role/permission arrays to payload (preserving order)
        dualRolePayload.sponsor_user_subroles = sponsor_user_subroles;
        dualRolePayload.sponsor_user_permissions = sponsor_user_permissions;

        createProviderDualRole(dualRolePayload, {
          onSuccess: (res) => {
            setIsLoading(false);
            setIsSuccess(true);
            setMessage("Organization with dual role created successfully!");
            setAlertOpen(true);

            // Get created sponsor info from response when available
            const createdSponsorId =
              res?.data?.sponsor_id ||
              res?.sponsor_id ||
              dualRolePayload.sponsor_id ||
              null;
            const createdSponsorName =
              res?.data?.sponsor_name ||
              res?.sponsor_name ||
              dualRolePayload.sponsor_name ||
              data.organizationName;

            // If there are selected users, call update API for each AFTER dual role creation
            const usersToUpdate = selectedUsersWithRoles || [];
            if (usersToUpdate.length === 0) {
              setTimeout(() => {
                navigate("/Backend/Users/Sponsors");
              }, 2000);
              return;
            }

            let remaining = usersToUpdate.length;

            usersToUpdate.forEach((u) => {
              const userObj = providerDetailsWithUsers?.contacts?.find(
                (c) => c.contactId === u.userId || c.contactId === u.userId
              );
              if (!userObj) {
                remaining -= 1;
                if (remaining === 0) {
                  setTimeout(() => navigate("/Backend/Users/Sponsors"), 2000);
                }
                return;
              }

              const roleName = u.role;

              // Build sponsor_user_subroles
              const sponsor_user_subroles = [
                {
                  sponsor_subrole_id: generateGuid(),
                  sub_role: roleName,
                },
              ];

              // Build sponsor_user_permissions from roleDefinitions (sponsor roles)
              // Note: roleDefinitions contains sponsor roles like "Sponsor Admin", "RFI Manager", "VQA Manager"
              const rightsList = roleDefinitions[roleName]?.defaultRights || [];
              const sponsor_user_permissions = rightsList.map((r) => ({
                rights: r,
                sponsor_rights_id: generateGuid(),
              }));

              // Determine hc_respond_role based on role name
              let hcRespondRole = "Admin";
              if (roleName === "RFI Manager - Approver") {
                hcRespondRole = "Approver";
              } else if (roleName === "RFI Manager - User") {
                hcRespondRole = "User";
              }

              // Determine hc_duediligence_role based on role name
              let hcDuediligenceRole = "Admin";
              if (roleName.includes("RFI Manager")) {
                hcDuediligenceRole = "Editor";
              }

              // Compose payload matching update API structure
              const updatePayload = {
                email: userObj.email,
                phone_number: userObj.phone || userObj.phoneNumber || null,
                office_phone_number: userObj.officePhoneNumber || null,
                first_name: userObj.firstName || "",
                last_name: userObj.lastName || "",
                primary: !!userObj.isOwner,
                org_id: selectedOrgId || userObj.org_id || null,
                sponsor_user_subroles,
                sponsor_user_permissions,
                sponsor_id: createdSponsorId,
                sponsor_name: createdSponsorName,
                hc_duediligence_role: hcDuediligenceRole,
                hc_respond_role: hcRespondRole,
              };

              const clerkId =
                userObj.user_clerk_id ||
                userObj.userClerkId ||
                userObj.user_clerk ||
                null;

              updateProviderUserRoles(
                { id: clerkId, roleData: updatePayload },
                {
                  onSuccess: () => {
                    remaining -= 1;
                    if (remaining === 0) {
                      setTimeout(
                        () => navigate("/Backend/Users/Sponsors"),
                        2000
                      );
                    }
                  },
                  onError: (err) => {
                    console.error("Error updating user role:", err);
                    remaining -= 1;
                    if (remaining === 0) {
                      setTimeout(
                        () => navigate("/Backend/Users/Sponsors"),
                        2000
                      );
                    }
                  },
                }
              );
            });
          },
          onError: (error) => {
            setIsLoading(false);
            setIsSuccess(false);
            setMessage(
              error.message || "Error creating organization with dual role"
            );
            setAlertOpen(true);
          },
        });
      } else {
        // Otherwise, create a new sponsor using the regular flow
        createSponsor(
          { data: sponsorData },
          {
            onSuccess: () => {
              setIsLoading(false);
              setIsSuccess(true);
              setMessage("Organization created successfully!");
              setAlertOpen(true);
              setTimeout(() => {
                navigate("/Backend/Users/Sponsors");
              }, 2000);
            },
            onError: (error) => {
              setIsLoading(false);
              setIsSuccess(false);
              setMessage(error.message || "Error creating organization");
              setAlertOpen(true);
            },
          }
        );
      }
    }
  };

  // Delete handler for an existing sponsor
  const handleDelete = () => {
    deleteSponsor(id, {
      onSuccess: () => {
        setIsSuccess(true);
        setMessage("Organization deleted successfully!");
        setAlertOpen(true);
        setTimeout(() => {
          navigate("/Backend/Users/Sponsors");
        }, 2000);
      },
      onError: (error) => {
        setIsSuccess(false);
        setMessage(error.message || "Error deleting organization");
        setAlertOpen(true);
      },
    });
  };

  return (
    <div className="w-full flex justify-center px-4">
      <NotificationModal
        open={alertOpen}
        setOpen={setAlertOpen}
        desc={message}
        title={isSuccess ? "Success" : "Error"}
        error={!isSuccess}
      />

      {/* Form container with half-width and centered */}
      <div className="w-full max-w-2xl bg-white shadow-sm rounded-lg">
        <form onSubmit={handleSubmit(onSubmit)} className="w-full p-6">
          {/* Header */}
          <div className="pb-5 border-b border-gray-200">
            <h1 className="font-semibold text-xl">
              {id ? "Update Organization" : "Add New Organization"}
            </h1>
            <p className="text-sm text-gray-600">All fields are required.</p>
          </div>

          {/* Organization Information */}
          <div className="mt-6 space-y-6">
            <div className="border-b border-gray-200 pb-6">
              <FormInput
                label="Existing Vendor Account"
                name="vendorOrganization"
                register={register}
                setValue={setValue}
                watch={watch}
                errors={errors}
                type="select"
                options={providerOrgData || []}
                disabled={!!id}
                onClear={clearVendorFields}
              />

              {/* Show users grid only in create mode when vendor is selected */}
              {!id && selectedProviderId && selectedOrgId && (
                <OrganizationUsersGrid
                  providerId={selectedProviderId}
                  orgId={selectedOrgId}
                  providerData={providerDetailsWithUsers}
                  isLoading={isProviderDetailsLoading}
                  isError={isProviderDetailsError}
                  onRoleChange={handleUserRoleChange}
                  roleType="sponsor"
                />
              )}
            </div>
            <div className="border-b border-gray-200 pb-6">
              <FormInput
                label="Full Organization Name"
                name="organizationName"
                register={register}
                errors={errors}
                placeholder="Enter organization name"
                disabled={
                  id
                    ? true
                    : !id &&
                      selectedVendorOrg &&
                      isProviderDetailsLoaded &&
                      providerOrgDetails
                }
              />
            </div>

            <div className="border-b border-gray-200 pb-6">
              <label className="block text-sm font-medium text-gray-900 mb-1">
                Company Website
              </label>
              <Controller
                name="companyWebsite"
                control={control}
                render={({ field }) => (
                  <HttpsUrlInputStandalone
                    name="companyWebsite"
                    label=""
                    placeholder="www.vendorwebsite.com"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    errorMessage={errors.companyWebsite?.message}
                    disabled={
                      !id &&
                      selectedVendorOrg &&
                      isProviderDetailsLoaded &&
                      providerOrgDetails
                    }
                  />
                )}
              />
            </div>

            <div className="border-b border-gray-200 pb-6">
              <div className="flex flex-col">
                <label className="block text-sm font-medium text-gray-900">
                  Office Number
                </label>
                <input
                  type="tel"
                  {...register("officeNumber")}
                  placeholder="e.g. 1-234-567-8900"
                  onInput={handleOfficeNumberInput}
                  maxLength={14}
                  className="mt-1 w-full border-gray-300 rounded-md text-sm"
                />
                {/* Office number is optional, show error only if present and invalid */}
                {errors.officeNumber && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.officeNumber.message}
                  </p>
                )}
              </div>
            </div>

            <div className="border-b border-gray-200 pb-6">
              <FormInput
                label="Company Email"
                name="companyEmail"
                register={register}
                errors={errors}
                type="email"
                placeholder="company@example.com"
                disabled={
                  !id &&
                  selectedVendorOrg &&
                  isProviderDetailsLoaded &&
                  providerOrgDetails
                }
              />
            </div>

            <div className="border-b border-gray-200 pb-6">
              <FormInput
                label="Company Type"
                name="companyType"
                register={register}
                errors={errors}
                type="select"
                options={[
                  { value: "corporation", label: "Corporation" },
                  { value: "llc", label: "Limited Liability Company (LLC)" },
                  { value: "partnership", label: "Partnership" },
                  { value: "soleProprietorship", label: "Sole Proprietorship" },
                  { value: "nonprofit", label: "Non-Profit Organization" },
                  { value: "government", label: "Government Agency" },
                  { value: "educational", label: "Educational Institution" },
                  { value: "other", label: "Other" },
                ]}
              />
            </div>

            {/* Primary Company Location */}
            <div className="pb-5 border-b border-gray-200">
              <h2 className="font-semibold text-lg">
                Primary Company Location
              </h2>
            </div>

            <div className="border-b border-gray-200 pb-6">
              <FormInput
                label="Country"
                name="country"
                register={register}
                errors={errors}
                type="select"
                options={
                  countries || [
                    { value: "US", label: "United States" },
                    { value: "CA", label: "Canada" },
                    { value: "UK", label: "United Kingdom" },
                    { value: "OTHER", label: "Other" },
                  ]
                }
              />
            </div>

            <div className="border-b border-gray-200 pb-6">
              <FormInput
                label="Street Address"
                name="streetAddress"
                register={register}
                errors={errors}
                placeholder="Enter street address"
              />
            </div>

            {/* City, State, Zip in a single row */}
            <div className="grid grid-cols-3 gap-4 border-b border-gray-200 pb-6">
              <FormInput
                label="City"
                name="city"
                register={register}
                errors={errors}
                placeholder="City"
                className="col-span-1"
              />
              <FormInput
                label="State/Province"
                name="state"
                register={register}
                errors={errors}
                placeholder="State"
                className="col-span-1"
              />
              <FormInput
                label="Zip/Postal Code"
                name="zipCode"
                register={register}
                errors={errors}
                placeholder="Zip code"
                className="col-span-1"
              />
            </div>

            {/* Form Buttons */}
            <div className="flex justify-end gap-4 pt-4">
              <button
                type="button"
                onClick={() => navigate("/Backend/Users/Sponsors")}
                className="rounded-md px-4 py-2 text-sm font-semibold text-gray-700 border border-gray-300 hover:bg-gray-100 focus:outline-none"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!isDirty || isLoading}
                className={`rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm focus:outline-none ${
                  !isDirty || isLoading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-indigo-600 hover:bg-indigo-500"
                }`}
              >
                {isLoading ? "Saving..." : id ? "Update" : "Save"}
              </button>
              {id && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isDeleteLoading}
                  className="rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm focus:outline-none bg-red-600 hover:bg-red-500"
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SponsorForm;
